import { type AttributeValue, type Attributes, type Meter, metrics } from '@opentelemetry/api';
import { type LogAttributes, type Logger, SeverityNumber, logs } from '@opentelemetry/api-logs';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { LoggerProvider } from '@opentelemetry/sdk-logs';
import { MeterProvider } from '@opentelemetry/sdk-metrics';
import { ATTR_SERVICE_INSTANCE_ID, ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import type { LogLevel, TelemetryConfig } from '../types';

// Instrumentation-scope identity for the library itself. This is distinct from
// the *service* resource (service.name / service.version), which the consuming
// application supplies via TelemetryConfig.
const METER_NAME = '@seeqdev/telemetry';
const METER_VERSION = '0.0.1';

// Maps the webserver-style log levels to OTel severity numbers. severityText is
// derived from the level itself (uppercased) at emit time.
const LEVEL_TO_SEVERITY: Record<LogLevel, SeverityNumber> = {
  trace: SeverityNumber.TRACE,
  debug: SeverityNumber.DEBUG,
  info: SeverityNumber.INFO,
  warn: SeverityNumber.WARN,
  error: SeverityNumber.ERROR,
  fatal: SeverityNumber.FATAL,
};

// The SDK is a process-wide singleton held in module scope. We model it with
// free functions + module state (the `@seeqdev/api` axios.utilities.ts
// convention) rather than a class that polices its own single-instance
// lifecycle.
let meterProvider: MeterProvider | null = null;
let meter: Meter | null = null;
let loggerProvider: LoggerProvider | null = null;
let logger: Logger | null = null;
let commonAttributes: Attributes = {};
let serviceInstanceId: string | undefined;

/**
 * Random UUID v4 for `service.instance.id`, or `undefined` when `crypto.randomUUID` is unavailable. It only exists
 * in secure contexts (https / localhost), which every live Seeq deployment is; {@link initTelemetrySdk} reports a
 * missing id through the OTel logs pipeline once that is up.
 */
function generateServiceInstanceId(): string | undefined {
  return typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : undefined;
}

/**
 * Initialize the telemetry SDK from the supplied config: builds the OTel MeterProvider (resource = service.name /
 * service.version / service.instance.id / custom attributes) and registers it as the global provider.
 *
 * `service.instance.id` is generated here, once per SDK instance, and is not part of the config. In the browser every
 * tab (document) runs its own SDK and so is its own OTel service instance; a unique id per instance is what keeps
 * concurrent tabs from writing the same metric stream (the OTel single-writer rule), which the collector would
 * otherwise read as endless counter resets. It is deliberately not persisted across reloads: a reload restarts the SDK
 * with zeroed counters, i.e. a new instance.
 *
 * The id is kept OFF the logs resource. Loki indexes resource attributes such as service.instance.id as stream labels,
 * so a per-tab id there would open a new set of streams per page load. {@link emitLog} attaches it to each record as
 * an attribute instead, which Loki stores as structured metadata: not indexed, still filterable after a pipe.
 *
 * Idempotent — a second call with the SDK already initialized is a no-op and keeps the existing provider. Callers
 * that want re-init to be an error should go through {@link setupTelemetry}.
 */
export function initTelemetrySdk(config: TelemetryConfig): void {
  if (meterProvider) {
    return;
  }

  serviceInstanceId = generateServiceInstanceId();
  const serviceAttributes = {
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.serviceVersion,
    ...config.resourceAttributes,
  };
  const metricsResource = resourceFromAttributes({
    ...serviceAttributes,
    [ATTR_SERVICE_INSTANCE_ID]: serviceInstanceId,
  });
  const logsResource = resourceFromAttributes(serviceAttributes);

  meterProvider = new MeterProvider({ resource: metricsResource, readers: config.readers });
  metrics.setGlobalMeterProvider(meterProvider);
  meter = metrics.getMeter(METER_NAME, METER_VERSION);

  // Logs are opt-in: only stand up a LoggerProvider when the consumer supplies log record processors (exporters),
  // mirroring the metric-readers contract.
  if (config.logRecordProcessors && config.logRecordProcessors.length > 0) {
    loggerProvider = new LoggerProvider({ resource: logsResource, processors: config.logRecordProcessors });
    logs.setGlobalLoggerProvider(loggerProvider);
    logger = logs.getLogger(METER_NAME, METER_VERSION);
  }

  // Reported through the OTel logs pipeline (i.e. Loki in live environments) rather than the console: that is where
  // someone diagnosing colliding browser metrics will be looking. No-op when the consumer configured no log
  // processors, which is also the only case where this could go unnoticed.
  if (serviceInstanceId === undefined) {
    emitLog(
      'warn',
      'crypto.randomUUID is unavailable (insecure context?); service.instance.id was omitted, ' +
        'so metrics from concurrent browser tabs share one stream and will collide.',
    );
  }
}

/** Whether {@link initTelemetrySdk} has run and the provider is live. */
export function isTelemetrySdkInitialized(): boolean {
  return meterProvider !== null;
}

/** The library Meter, or `null` before initialization. */
export function getMeter(): Meter | null {
  return meter;
}

/** The library Logger, or `null` before init / when no log processors were configured. */
export function getLogger(): Logger | null {
  return logger;
}

/**
 * Emit an OTel log record at the given level. No-op before init or when no log record processors were configured.
 * `severityText` is the uppercased level. The per-tab `service.instance.id` rides along as a record attribute (see
 * {@link initTelemetrySdk} for why it is not on the resource); an explicit attribute of the same name wins.
 */
export function emitLog(level: LogLevel, body: string, attributes?: LogAttributes): void {
  logger?.emit({
    severityNumber: LEVEL_TO_SEVERITY[level],
    severityText: level.toUpperCase(),
    body,
    attributes:
      serviceInstanceId === undefined ? attributes : { [ATTR_SERVICE_INSTANCE_ID]: serviceInstanceId, ...attributes },
  });
}

/**
 * Set an attribute applied to every metric recording that reads
 * {@link getCommonAttributes} (e.g. the current route). Read lazily at record
 * time so instruments always see the latest value.
 */
export function setCommonAttribute(key: string, value: AttributeValue): void {
  commonAttributes = { ...commonAttributes, [key]: value };
}

export function getCommonAttributes(): Attributes {
  return commonAttributes;
}

/** Flush all readers and log processors immediately (e.g. on page hide). No-op before init. */
export async function forceFlush(): Promise<void> {
  await Promise.all([meterProvider?.forceFlush(), loggerProvider?.forceFlush()]);
}

/** Shut down the providers and clear all module state. */
export async function shutdown(): Promise<void> {
  await Promise.all([meterProvider?.shutdown(), loggerProvider?.shutdown()]);
  meterProvider = null;
  meter = null;
  loggerProvider = null;
  logger = null;
  commonAttributes = {};
  serviceInstanceId = undefined;
}

/**
 * Visible for testing only. Synchronously clears module state without shutting
 * the providers down (tests that need a clean teardown should `await shutdown()`
 * first).
 */
export function resetTelemetrySdkForTesting(): void {
  meterProvider = null;
  meter = null;
  loggerProvider = null;
  logger = null;
  commonAttributes = {};
  serviceInstanceId = undefined;
}
